// Order-related filters

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createRadioFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const orderFilters: FilterRegistry = {
	orderKeywords: {
		...createKeywordFilter({
			label: 'Search Orders',
			placeholder:
				'Search by order ID, external ID, customer PO, or client name...',
			objectTable: 'order',
			// PD-14968 — 'reports' lets the Order Reconciliation report offer
			// the same order/client search the Orders list has.
			listKeys: ['order', 'order-client-embedded', 'reports'],
			fulltextFields: [
				'externalId',
				'externalId2',
				'_id',
				'customerPO',
				'c.name',
				'c.externalId',
				'c.externalId2',
			],
		}),
		queryParamName: 'orderKeywords',
	},

	// Status options are loaded from the API so the dropdown reflects the
	// distinct values actually present in `order.status` for this tenant —
	// including tenant-specific custom string statuses that don't match the
	// standard OrderStatus enum. Known enum codes (e.g. '1', '2') are
	// translated to friendly labels server-side; custom strings pass through
	// as their own label. The synthetic "Requires Attention" pseudo-status
	// (handled specially by the backend filter builder) is always appended
	// to the API response so it remains selectable.
	orderStatus: {
		type: 'multi-select',
		label: 'Order Status',
		placeholder: 'Select order status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/orders/distinct-statuses',
		queryParamName: 'orderStatus',
		multiple: true,
		// PD-14109: count reflects the currently filtered Orders list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'order',
			fieldName: 'status',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		// PD-14968 — 'reports' lets the Order Reconciliation report use this
		// filter. transformFiltersForBackend drops any filter whose listKeys
		// exclude the caller's list key, so without it a status selection made
		// on a report could silently never reach the query.
		listKeys: ['order', 'order-client-embedded', 'reports'],
	},

	orderOrigin: {
		...createStatusFilter({
			label: 'Order Origin',
			options: [
				{ id: '1', label: 'CRM' },
				{ id: '2', label: 'ERP' },
				{ id: '3', label: 'E-Commerce' },
			],
			objectTable: 'order',
			fieldName: 'origin',
			listKeys: ['order', 'order-client-embedded'],
			colorScheme: 'green',
		}),
		queryParamName: 'orderOrigin',
	},

	// PD-17225 — the warehouse (branch) the order is credited to.
	orderWarehouse: {
		...createEntityLookupFilter({
			label: 'Warehouse',
			placeholder: 'Search warehouses...',
			apiEndpoint: '/warehouses',
			objectTable: 'order',
			fieldName: 'fk_warehouse__id',
			listKeys: ['order', 'order-client-embedded'],
			displayFields: {
				id: 'id',
				primary: 'name',
			},
			colorScheme: 'purple',
		}),
		queryParamName: 'orderWarehouse',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	orderCompany: {
		...createEntityLookupFilter({
			label: 'Company',
			placeholder: 'Search companies...',
			apiEndpoint: '/users/companies',
			objectTable: 'order',
			fieldName: 'fk_company__id',
			listKeys: ['order', 'order-client-embedded'],
			displayFields: {
				id: 'id',
				primary: 'label',
			},
			colorScheme: 'purple',
		}),
		queryParamName: 'orderCompany',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	orderDateRange: {
		...createDateRangeFilter({
			label: 'Order Date',
			objectTable: 'order',
			dateField: 'date',
			listKeys: ['order', 'order-client-embedded'],
		}),
		queryParamName: 'orderDateRange',
	},

	orderModifiedDateRange: {
		...createDateRangeFilter({
			label: 'Last Modified Date',
			objectTable: 'order',
			dateField: 'modifiedDate',
			listKeys: ['order', 'order-client-embedded'],
		}),
		queryParamName: 'orderModifiedDateRange',
	},

	// PD-16787: the ERP's requested ("ship by") date, `order.shippedByDate`.
	// Registered so the Sales Helper's Order Search can filter on it ("orders
	// due to ship next week"); it is not on the order list's filter bar.
	orderRequestedDateRange: {
		...createDateRangeFilter({
			label: 'Ship By Date',
			objectTable: 'order',
			dateField: 'shippedByDate',
			listKeys: ['order', 'order-client-embedded'],
		}),
		queryParamName: 'orderRequestedDateRange',
	},

	orderSalesRep: {
		type: 'multi-select',
		// Despite the name, this filter scopes orders to those whose related
		// CLIENT is assigned to the selected salesperson (primary or alternate).
		// The SQL is built by the orderSalesRep handler in
		// apps/crm-api/src/shared/utils/typeormFilterBuilder.ts.
		label: 'Client Sales Rep',
		placeholder: 'Select sales representatives...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		queryParamName: 'orderSalesRep',
		multiple: true,
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryMapping: {
			objectTable: 'order',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['order', 'order-client-embedded'],
	},

	priceContractKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by contract name, item, or client...',
			objectTable: 'price_contract',
			listKeys: [
				'price_contract',
				'price-contract-client-embedded',
				'price-contract-item-embedded',
			],
		}),
		queryParamName: 'priceContractKeywords',
	},

	priceContractStatus: {
		...createStatusFilter({
			label: 'Status',
			options: [
				{ id: 'active', label: 'Active' },
				{ id: 'expired', label: 'Expired' },
				{ id: 'pending', label: 'Pending' },
				{ id: 'draft', label: 'Draft' },
			],
			objectTable: 'price_contract',
			fieldName: 'status',
			listKeys: [
				'price_contract',
				'price-contract-client-embedded',
				'price-contract-item-embedded',
			],
			colorScheme: 'green',
		}),
		queryParamName: 'priceContractStatus',
	},

	priceContractType: {
		...createRadioFilter({
			label: 'Type',
			options: [
				{ id: 'contracts', label: 'Price Contracts' },
				{ id: 'saved', label: 'Saved Prices' },
				// id must NOT be the literal 'all' — FilterService drops radio
				// values with that id (platform "All = no filter" convention),
				// and here an absent filter means 'contracts', not everything.
				{ id: 'any', label: 'All' },
			],
			// PD-15437 — saved prices (per-client `wsp_*` contracts) are now
			// listable, but default to hidden so the page is unchanged until
			// the user deliberately flips the filter.
			defaultValue: { id: 'contracts', label: 'Price Contracts' },
			objectTable: 'price_contract',
			// Nominal only — like `status` above, the price-contracts service
			// translates this filter itself (externalId pattern predicate)
			// rather than matching a column directly.
			fieldName: 'externalId',
			listKeys: ['price_contract'],
			colorScheme: 'blue',
		}),
		queryParamName: 'priceContractType',
	},

	/**
	 * PD-15127 — Order Reconciliation: narrow to one class of problem so it can
	 * be worked as a list.
	 *
	 * Report-only. The flags are derived from each order's payments when the
	 * report runs and are never stored, so this cannot be a column filter — the
	 * report service applies it to the rows it has just computed. No
	 * `queryMapping` for the same reason: there is no column to match.
	 *
	 * Keys mirror ReconciliationFlagModel. An order carrying two flags appears
	 * under either, and it leaves the filtered view as soon as it is put right.
	 */
	reconciliationFlags: {
		type: 'multi-select',
		label: 'Reconciliation Status',
		placeholder: 'Select what to chase...',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		options: [
			// PD-15337 — matches the chip the report renders ("Unpaid $X" /
			// "Partly paid — $X still owed"); the id stays put so saved
			// searches keep resolving.
			{ id: 'billed_not_collected', label: 'Unpaid' },
			// PD-15305 — a collection the gateway refused, nothing collected since.
			{ id: 'collection_refused', label: 'Collection refused' },
			// PD-15228 — collected on the order, not applied to its invoices.
			{ id: 'collected_not_applied', label: 'Collected, not applied' },
			{ id: 'not_billed', label: 'Delivered, not billed' },
			{ id: 'over_collected', label: 'Over-collected' },
			{ id: 'refund_owed', label: 'Refund owed' },
			// PD-15136 — one option for one situation. Replaces
			// 'billed_not_delivered' and 'status_discrepancy', which described
			// the same orders from two angles. Those ids are deliberately left
			// out rather than aliased: a saved filter still holding one simply
			// matches nothing, which is the honest outcome for a flag the report
			// no longer raises.
			{ id: 'unfulfilled', label: 'Unfulfilled' },
			{ id: 'duplicate_invoice', label: 'Invoiced more than once' },
			{ id: 'possible_unlinked_invoice', label: 'Possible unlinked invoice' },
			{ id: 'hold_expiring', label: 'Hold expiring' },
			{ id: 'pending_fulfilment', label: 'Pending fulfilment' },
			{ id: 'reconciled', label: 'Reconciled' },
		],
		queryParamName: 'reconciliationFlags',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		// PD-16475 — report-only. An empty list (with no queryMapping) made it
		// valid for EVERY list, so List Configuration offered "Reconciliation
		// Status" on Issues and elsewhere, where it filtered nothing.
		listKeys: ['reports'],
	},
};
